import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { cn } from '@/shared/lib';
import { Select } from '@/shared/ui/select';
import { usePagination } from '@/shared/ui/data-table/hooks';
import { dataTableStyles } from '@/shared/ui/data-table/styles';

/**
 * Page controls beneath the table.
 *
 * The range text states what is actually on screen — "21–30 of 42" — rather
 * than just a page number. With server-side paging the page number alone does
 * not tell a reader which records they are looking at, and after a filter
 * change it is actively misleading.
 *
 * The numeric page buttons (1 2 3 … 15) are the default. Compact mode drops
 * the numbers and the per-page selector, and keeps just the prev / next
 * chevrons plus the page text — for dense screens where the footer is a
 * sub-line, not a control surface.
 */
export function TableFooter({
  page,
  pageCount,
  pageSize,
  total,
  rowCount,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  compact = false,
}: {
  /** 1-based current page. */
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  /**
   * Options for the per-page selector. Pass an empty array (or omit) to hide
   * the selector entirely.
   */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <CompactFooter
        page={page}
        pageCount={pageCount}
        onPrevious={() => onPageChange(page - 1)}
        onNext={() => onPageChange(page + 1)}
      />
    );
  }

  return (
    <FullFooter
      page={page}
      pageCount={pageCount}
      pageSize={pageSize}
      total={total}
      rowCount={rowCount}
      onPageChange={onPageChange}
      pageSizeOptions={pageSizeOptions}
      onPageSizeChange={onPageSizeChange}
    />
  );
}

/**
 * The dense footer: just prev / next chevrons and the page text. For screens
 * where the pagination is a sub-line, not a control surface.
 */
function CompactFooter({
  page,
  pageCount,
  onPrevious,
  onNext,
}: {
  page: number;
  pageCount: number;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <div className={cn(dataTableStyles.footer, dataTableStyles.paginationCompact)}>
      <button
        type="button"
        className={dataTableStyles.pageButtonCompact}
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={onPrevious}
      >
        <FiChevronLeft size={14} aria-hidden="true" />
      </button>
      <span className={dataTableStyles.rangeText}>
        Page {page} of {pageCount}
      </span>
      <button
        type="button"
        className={dataTableStyles.pageButtonCompact}
        aria-label="Next page"
        disabled={page >= pageCount}
        onClick={onNext}
      >
        <FiChevronRight size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * The full footer: range + per-page on the left, numeric pagination on the right.
 *
 * Layout reads left-to-right: where am I? (`1–9 of 132`) — how much at a time?
 * (per-page) — where can I go? (numeric buttons). The three pieces answer
 * three different questions, and grouping them by question matters more than
 * grouping them by row.
 */
function FullFooter({
  page,
  pageCount,
  pageSize,
  total,
  rowCount,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
}: {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
}) {
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = total === 0 ? 0 : first + rowCount - 1;
  const showSizeSelector = pageSizeOptions !== undefined && pageSizeOptions.length > 0 && onPageSizeChange;

  return (
    <div className={dataTableStyles.footer}>
      <nav className={dataTableStyles.pagination} aria-label="Pagination">
        <PrevNextButton direction="prev" disabled={page <= 1} onClick={() => onPageChange(page - 1)} />
        <PageWindow page={page} pageCount={pageCount} onPageChange={onPageChange} />
        <PrevNextButton
          direction="next"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        />
      </nav>

      <div className={dataTableStyles.footerTrailing}>
        {showSizeSelector ? (
          <PageSizeSelector
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions ?? []}
            onPageSizeChange={onPageSizeChange ?? (() => undefined)}
          />
        ) : null}
        <span className={dataTableStyles.rangeText}>
          {first}–{last} of {total}
        </span>
      </div>
    </div>
  );
}

/** "Show N per page" — the per-page selector with its surrounding label. */
function PageSizeSelector({
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}: {
  pageSize: number;
  pageSizeOptions: number[];
  onPageSizeChange: (pageSize: number) => void;
}) {
  return (
    <div className={dataTableStyles.pageSizeGroup}>
      <span className={dataTableStyles.rangeText}>Show</span>
      <Select
        label="Rows per page"
        hideLabel
        size="sm"
        value={String(pageSize)}
        onChange={(event) => onPageSizeChange(Number(event.target.value))}
        options={pageSizeOptions.map((value) => ({ value: String(value), label: String(value) }))}
        className={dataTableStyles.pageSizeSelect}
      />
      <span className={dataTableStyles.rangeText}>per page</span>
    </div>
  );
}

/** The chevron button on either end of the numeric pagination. */
function PrevNextButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'prev' ? FiChevronLeft : FiChevronRight;
  return (
    <button
      type="button"
      className={dataTableStyles.pageButtonCompact}
      aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon size={14} aria-hidden="true" />
    </button>
  );
}

/** Numeric page buttons, with leading / trailing ellipses when the page count overflows the slot budget. */
function PageWindow({
  page,
  pageCount,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) {
  const { pages, hasLeadingEllipsis, hasTrailingEllipsis } = usePagination(page, pageCount);

  return (
    <>
      {hasLeadingEllipsis ? (
        <>
          <PageButton pageNumber={1} isCurrent={false} onClick={() => onPageChange(1)} />
          <span className={dataTableStyles.ellipsis} aria-hidden="true">
            …
          </span>
        </>
      ) : null}

      {pages.map((pageNumber) => (
        <PageButton
          key={pageNumber}
          pageNumber={pageNumber}
          isCurrent={pageNumber === page}
          onClick={() => onPageChange(pageNumber)}
        />
      ))}

      {hasTrailingEllipsis ? (
        <>
          <span className={dataTableStyles.ellipsis} aria-hidden="true">
            …
          </span>
          <PageButton pageNumber={pageCount} isCurrent={false} onClick={() => onPageChange(pageCount)} />
        </>
      ) : null}
    </>
  );
}

/** A single numeric page button. The current page is announced as such. */
function PageButton({
  pageNumber,
  isCurrent,
  onClick,
}: {
  pageNumber: number;
  isCurrent: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        dataTableStyles.pageButtonCompact,
        isCurrent ? dataTableStyles.pageButtonCurrent : undefined,
      )}
      aria-label={`Page ${pageNumber}`}
      aria-current={isCurrent ? 'page' : undefined}
      onClick={onClick}
    >
      {pageNumber}
    </button>
  );
}
